import { Injectable } from '@angular/core';

export type CurrencyAmountSource = 'local' | 'reference';

/** Estado de moeda/valores de um voucher (ou de um form único), independente de onde os
 *  campos moram — cada chamador lê do seu próprio objeto/campos e escreve de volta o
 *  resultado, sem o service precisar conhecer o shape real (voucher de array, campos soltos
 *  de um form, etc.). */
export interface CurrencyToggleAmounts {
  currencyId?: number | null;
  /** Fallback de identificação da moeda quando o id ainda não foi resolvido (ex.: contexto
   *  de template) — mesma regra que já existia no voucher-list.component. */
  currencyName?: string | null;
  amountSource?: CurrencyAmountSource | null;
  localAmount?: number | null;
  referenceAmount?: number | null;
}

export interface CurrencyToggleContext {
  idLocalCurrency?: number | null;
  idReferenceCurrency?: number | null;
  exchangeRate?: number | null;
  isSameCurrency: boolean;
}

/**
 * AB#312: regra de toggle de moeda (local x referência) — qual lado fica editável e como
 * converter o valor pelo câmbio — extraída do voucher-list.component (usado na criação do
 * Quote) pra ser reaproveitada também pelos componentes de DAVoucher (da-voucher-list,
 * da-voucher-modal), evitando 3 cópias da mesma regra.
 *
 * Sem estado de propósito: cada método recebe o estado atual e devolve um novo estado, sem
 * mutar nada — funciona tanto pra uma linha de tabela (objeto por voucher) quanto pra um form
 * único (campos soltos de componente), já que quem chama decide onde ler/escrever.
 */
@Injectable({ providedIn: 'root' })
export class CurrencyToggleService {
  static readonly LOCAL_AMOUNT_SOURCE: CurrencyAmountSource = 'local';
  static readonly REFERENCE_AMOUNT_SOURCE: CurrencyAmountSource = 'reference';

  /** True quando a moeda ativa hoje é a de referência (decide qual campo fica readonly). */
  isReferenceCurrency(
    state: Pick<CurrencyToggleAmounts, 'currencyId' | 'currencyName'>,
    ctx: CurrencyToggleContext,
  ): boolean {
    if (ctx.idReferenceCurrency && state.currencyId) {
      return state.currencyId === ctx.idReferenceCurrency;
    }
    // Fallback por nome (contexto de template, antes dos ids serem resolvidos).
    if (state.currencyName) {
      const n = state.currencyName.toUpperCase();
      return n.includes('DOLAR') || n.includes('USD');
    }
    return false;
  }

  inferAmountSource(state: CurrencyToggleAmounts, ctx: CurrencyToggleContext): CurrencyAmountSource {
    if (ctx.isSameCurrency) return CurrencyToggleService.LOCAL_AMOUNT_SOURCE;
    return this.isReferenceCurrency(state, ctx)
      ? CurrencyToggleService.REFERENCE_AMOUNT_SOURCE
      : CurrencyToggleService.LOCAL_AMOUNT_SOURCE;
  }

  /** Clique no botão de moeda: troca qual lado fica ativo e reconverte o outro a partir dele. */
  setCurrencyById(
    state: CurrencyToggleAmounts,
    currencyId: number,
    ctx: CurrencyToggleContext,
  ): CurrencyToggleAmounts {
    const amountSource =
      currencyId === ctx.idLocalCurrency
        ? CurrencyToggleService.LOCAL_AMOUNT_SOURCE
        : CurrencyToggleService.REFERENCE_AMOUNT_SOURCE;
    return this.syncAmountsFromSource({ ...state, currencyId, amountSource }, ctx);
  }

  /** Usuário digitou no campo de moeda local: vira a fonte e recalcula o de referência. */
  onLocalAmountInput(
    state: CurrencyToggleAmounts,
    value: number,
    ctx: CurrencyToggleContext,
  ): CurrencyToggleAmounts {
    return this.syncAmountsFromSource(
      {
        ...state,
        amountSource: CurrencyToggleService.LOCAL_AMOUNT_SOURCE,
        currencyId: ctx.idLocalCurrency,
        localAmount: value ?? 0,
      },
      ctx,
    );
  }

  /** Usuário digitou no campo de moeda de referência: vira a fonte e recalcula o local. */
  onReferenceAmountInput(
    state: CurrencyToggleAmounts,
    value: number,
    ctx: CurrencyToggleContext,
  ): CurrencyToggleAmounts {
    return this.syncAmountsFromSource(
      {
        ...state,
        amountSource: CurrencyToggleService.REFERENCE_AMOUNT_SOURCE,
        currencyId: ctx.idReferenceCurrency,
        referenceAmount: value ?? 0,
      },
      ctx,
    );
  }

  /** Recalcula o campo "passivo" a partir do campo/moeda ativos (amountSource), usando o
   *  câmbio informado. Não muta `state` — devolve um objeto novo. */
  syncAmountsFromSource(state: CurrencyToggleAmounts, ctx: CurrencyToggleContext): CurrencyToggleAmounts {
    const amountSource = state.amountSource ?? this.inferAmountSource(state, ctx);
    const rate = +(ctx.exchangeRate ?? 0) || 0;

    if (amountSource === CurrencyToggleService.REFERENCE_AMOUNT_SOURCE) {
      return {
        ...state,
        amountSource,
        currencyId: ctx.idReferenceCurrency,
        // Achado do review AB#312: sem câmbio definido (rate <= 0, estado normal de uma DA que
        // ainda não teve o Exchange Rate preenchido), preserva o valor existente do campo
        // derivado em vez de zerar — zerar aqui, combinado com o campo ficando readonly,
        // deixava o valor preso sem jeito de corrigir pela tela.
        localAmount: rate > 0 ? this.round2((state.referenceAmount ?? 0) * rate) : (state.localAmount ?? state.referenceAmount ?? 0),
      };
    }

    if (ctx.isSameCurrency) {
      return {
        ...state,
        amountSource,
        currencyId: ctx.idLocalCurrency,
        referenceAmount: state.localAmount ?? 0,
      };
    }

    return {
      ...state,
      amountSource,
      currencyId: ctx.idLocalCurrency,
      // Mesmo raciocínio do ramo acima, pro sentido local -> referência.
      referenceAmount: rate > 0 ? this.round2((state.localAmount ?? 0) / rate) : (state.referenceAmount ?? state.localAmount ?? 0),
    };
  }

  round2(n: number): number {
    return parseFloat((n || 0).toFixed(2));
  }
}
